<script setup>
// M26.5.1 Part 8 — premium 6-state status system, replacing the plain
// active/paused/disabled badge. `liveStatus` is derived once per unit in
// AiWorkforcePage.vue's deriveLiveStatus() from fields that already exist
// (status, queueCount, activeCount, lastActivity, currentlyWorkingOn) — this
// component only renders it.
import { computed } from 'vue';

const props = defineProps({
  liveStatus: { type: String, required: true },
});

const META = {
  LIVE: {
    label: 'Live',
    dot: 'bg-success-500',
    classes:
      'bg-success-50 text-success-800 border-success-200 dark:bg-success-800/20 dark:text-success-400 dark:border-success-500/30',
    pulse: true,
  },
  PROCESSING: {
    label: 'Processing',
    dot: 'bg-woot-500',
    classes:
      'bg-woot-50 text-woot-700 border-woot-200 dark:bg-woot-500/10 dark:text-woot-400 dark:border-woot-500/30',
    pulse: true,
  },
  BUSY: {
    label: 'Busy',
    dot: 'bg-amber-500',
    classes:
      'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-500/30',
    pulse: false,
  },
  WAITING: {
    label: 'Waiting',
    dot: 'bg-cyan-500',
    classes:
      'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-400 dark:border-cyan-500/30',
    pulse: false,
  },
  PAUSED: {
    label: 'Paused',
    dot: 'bg-n-slate-7',
    classes: 'bg-n-alpha-2 text-n-slate-8 border-n-weak',
    pulse: false,
  },
  OFFLINE: {
    label: 'Offline',
    dot: 'bg-n-slate-6',
    classes: 'bg-n-alpha-2 text-n-slate-7 border-n-weak',
    pulse: false,
  },
};

const meta = computed(() => META[props.liveStatus] || META.OFFLINE);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <span
    class="inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full font-medium border capitalize shrink-0"
    :class="meta.classes"
  >
    <span class="relative flex size-1.5">
      <span
        v-if="meta.pulse"
        class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
        :class="meta.dot"
      />
      <span
        class="relative inline-flex rounded-full size-1.5"
        :class="meta.dot"
      />
    </span>
    {{ meta.label }}
  </span>
</template>
